import React, { FC } from "react";
import { graphql, PageProps } from "gatsby";
import { Helmet } from "react-helmet";
import Heading, { HeadingWeight } from "../../components/Heading";
import Text, { TextSize } from "../../components/Text";
import PostToc, { TOCItem } from "./PostToc";
import PostMarkdown from "./PostMarkdown";
import useWindowSize from "../../hooks/useWindowSize";
import Sidebar from "../../components/Sidebar";
import { formatPostsByCategory } from "../../utils/posts";
import useMainContext from "../../hooks/useMainContext";
import Footer from "../../components/Footer";

interface IPostLayoutProps extends PageProps {
  data: {
    mdx: {
      frontmatter: {
        title: string;
        description: string;
      };
      body: string;
      tableOfContents: { items: TOCItem[] };
      slug: string;
      parent: {
        mtime: string;
      };
    };
    allSidebarYaml: any;
  };
  pageContext: {
    prevPost: any;
    nextPost: any;
  };
}

const PostLayout: FC<IPostLayoutProps> = ({ data, pageContext }) => {
  const { category } = useMainContext();
  const { prevPost, nextPost } = pageContext;
  const { frontmatter, body, tableOfContents, parent, slug } = data.mdx;
  const { title, description } = frontmatter;
  const { width } = useWindowSize();
  const showToc = width > 900;
  const navItems = formatPostsByCategory(data.allSidebarYaml.nodes, category);

  return (
    <>
      <Sidebar navItems={navItems} />
      <div className="kld-post">
        <Helmet>
          <title>{`Qwilr API | ${title}`}</title>
          <meta name="description" content={description} />
        </Helmet>
        <article className="kld-post__page">
          <div className="kld-post__header">
            <Heading className="kld-post__title" level={1} weight={HeadingWeight.Strong}>
              {title}
            </Heading>
            <Text className="kld-post__description" as="p" size={TextSize.XXLarge}>
              {description}
            </Text>
          </div>
          <PostMarkdown>{body}</PostMarkdown>
        </article>
        {showToc && <PostToc contents={tableOfContents.items} />}
        <Footer className="kld-post__footer" prevPost={prevPost} nextPost={nextPost} modified={parent.mtime} />
      </div>
    </>
  );
};

export const query = graphql`
  query PostBySlug($slug: String!) {
    mdx(fields: { slug: { eq: $slug } }) {
      frontmatter {
        title
        description
      }
      slug
      body
      tableOfContents
      parent {
        ... on File {
          mtime
        }
      }
    }
    allSidebarYaml {
      nodes {
        label
        categories {
          label
          pages
        }
      }
    }
  }
`;

export default PostLayout;
